'use client';

import { X } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { cn } from '@/libs/tailwind';
import { generatePdfThumbnail, isPdfThumbnailSupported } from '@/utils/pdfThumbnail';

export type FileThumbnailProps = {
  id: string;
  file: File;
  url: string;
  isHovering?: boolean;
  onRemove?: (id: string) => void;
  onClick?: () => void;
  className?: string;
  // External hover state management (optional)
  onHoverChange?: (id: string, isHovering: boolean) => void;
};

export function FileThumbnail({
  id,
  file,
  url,
  isHovering = false,
  onRemove,
  onClick,
  className,
  onHoverChange,
}: FileThumbnailProps) {
  const [isHoveringInternal, setIsHoveringInternal] = useState(false);
  const [pdfThumbnail, setPdfThumbnail] = useState<string | null>(null);
  const isHovered = isHovering || isHoveringInternal;

  const isPdf = file.type === 'application/pdf';
  const isImage = file.type.startsWith('image/');
  const isPreviewable = isImage || isPdf;

  // Generate PDF thumbnail when component mounts with a PDF file
  useEffect(() => {
    const generateThumbnail = async () => {
      // Only attempt to generate thumbnail if:
      // 1. It's a PDF file
      // 2. We don't already have a thumbnail
      // 3. The browser supports PDF thumbnail generation
      if (isPdf && !pdfThumbnail && isPdfThumbnailSupported()) {
        try {
          const thumbnailUrl = await generatePdfThumbnail(file);
          if (thumbnailUrl) {
            setPdfThumbnail(thumbnailUrl);
          }
        } catch (err) {
          console.error('Error generating PDF thumbnail:', err);
        }
      }
    };

    generateThumbnail();
  }, [file, isPdf, pdfThumbnail]);

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(id);
    }
  };

  const handleMouseEnter = () => {
    setIsHoveringInternal(true);
    if (onHoverChange) {
      onHoverChange(id, true);
    }
  };

  const handleMouseLeave = () => {
    setIsHoveringInternal(false);
    if (onHoverChange) {
      onHoverChange(id, false);
    }
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={cn(
          'h-20 w-20 rounded-md bg-gray-50 flex items-center justify-center overflow-hidden transition-all',
          isHovered
            ? 'border-2 border-[#D3FB52]'
            : 'border border-gray-200',
          isPreviewable && onClick && 'cursor-pointer',
          className,
        )}
        onClick={isPreviewable && onClick ? onClick : undefined}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && isPreviewable && onClick) {
            e.preventDefault();
            onClick();
          }
        }}
        tabIndex={isPreviewable && onClick ? 0 : undefined}
        role={isPreviewable && onClick ? 'button' : undefined}
        aria-label={isPreviewable && onClick ? `Preview ${file.name}` : undefined}
      >
        {isImage ? (
          <div className="relative h-full w-full">
            <Image
              src={url}
              alt={file.name}
              fill
              sizes="80px"
              className="object-cover"
              priority={false}
            />
          </div>
        ) : isPdf ? (
          <div className="relative h-full w-full">
            {pdfThumbnail ? (
              <Image
                src={pdfThumbnail}
                alt={file.name}
                fill
                sizes="80px"
                className="object-cover"
                priority={false}
              />
            ) : (
              <div className="flex flex-col items-center justify-center px-1 h-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-10 w-10 text-red-500 mb-1"
                >
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <div className="text-xs text-center truncate w-full">
                  PDF
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-center px-1 truncate">
            {file.name}
          </div>
        )}
      </div>

      {isHovered && onRemove && (
        <button
          type="button"
          onClick={handleRemove}
          className="absolute -top-2 -right-2 bg-white rounded-full h-6 w-6 flex items-center justify-center shadow-md border border-gray-200 hover:bg-red-100 z-10"
        >
          <X className="h-4 w-4 text-gray-700" />
        </button>
      )}
    </div>
  );
}
